'use client';

import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/core/presentation/components/ui/Button';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  /**
   * Retorna `true` para fechar o modal após a ação (ex.: sucesso).
   * `false` mantém o modal aberto (ex.: erro tratado fora).
   */
  onConfirm: () => boolean | Promise<boolean>;
  confirmVariant?: 'primary' | 'danger';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  cancelLabel = 'Cancelar',
  confirmLabel = 'Confirmar',
  onConfirm,
  confirmVariant = 'primary',
}) => {
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!isOpen) setConfirming(false);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirming) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, confirming]);

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      const ok = await Promise.resolve(onConfirm());
      if (ok) onClose();
    } finally {
      setConfirming(false);
    }
  }, [onConfirm, onClose]);

  if (!isOpen) return null;

  const confirmButtonClassName =
    confirmVariant === 'danger' ?
      '!bg-red-600 !text-black !border-red-600 hover:!bg-red-700 hover:!border-red-700 hover:!text-black focus-visible:!ring-red-500/50'
    : '';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <button
        type="button"
        aria-label="Fechar"
        disabled={confirming}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 transition-opacity disabled:pointer-events-none"
      />
      <div
        className="relative w-full sm:max-w-md max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-xl bg-white border border-border-light shadow-xl flex flex-col mt-auto sm:mt-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-4 border-b border-border-light shrink-0">
          <h2 id="confirm-modal-title" className="text-lg font-semibold text-navy-dark pr-8">
            {title}
          </h2>
        </div>
        <div className="overflow-y-auto flex-1 p-4 sm:p-6 text-text-body">{children}</div>
        <div className="px-4 sm:px-6 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 border-t border-border-light shrink-0 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full sm:w-auto sm:min-w-[7rem]"
            disabled={confirming}
            onClick={onClose}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant === 'danger' ? 'outline' : 'primary'}
            size="sm"
            className={`w-full sm:w-auto sm:min-w-[10rem] ${confirmButtonClassName}`.trim()}
            disabled={confirming}
            loading={confirming}
            loadingLabel="Aguarde"
            onClick={() => void handleConfirm()}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
